"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { promotions } from "@/config/promotions";

type Phase = "intro" | "reveal" | "fade";

const OVERLAY_UNMOUNT_DELAY = 1500;

export function BlackFridayIntro() {
  if (!promotions.blackFridayEnabled) {
    return null;
  }

  return <BlackFridayIntroInner />;
}

function BlackFridayIntroInner() {
  const [phase, setPhase] = useState<Phase>("intro");
  const [isActive, setIsActive] = useState(true);
  const timersRef = useRef<number[]>([]);
  const finishedRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timerId) => {
      if (typeof window !== "undefined") {
        window.clearTimeout(timerId);
      }
    });
    timersRef.current = [];
  }, []);

  const finishIntro = useCallback(() => {
    if (finishedRef.current || typeof window === "undefined") {
      return;
    }
    finishedRef.current = true;
    setPhase("fade");
    const timeoutId = window.setTimeout(() => {
      setIsActive(false);
    }, OVERLAY_UNMOUNT_DELAY);
    timersRef.current.push(timeoutId);
  }, []);

  useEffect(() => {
    if (!isActive || typeof window === "undefined") {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    if (prefersReducedMotion.matches) {
      setIsActive(false);
      return;
    }

    setPhase("intro");
    const revealTimer = window.setTimeout(() => {
      setPhase("reveal");
    }, 600);
    const holdTimer = window.setTimeout(() => {
      setPhase("fade");
    }, 3600);
    const finishTimer = window.setTimeout(() => {
      finishIntro();
    }, 4200);

    timersRef.current.push(revealTimer, holdTimer, finishTimer);

    return () => {
      clearTimers();
    };
  }, [clearTimers, finishIntro, isActive]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  if (!isActive) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed inset-0 z-[120] bg-black text-white transition-opacity duration-[1500ms] ease-out",
        phase === "fade" ? "opacity-0 pointer-events-none" : "opacity-100"
      )}
    >
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-b from-black via-[#050709] to-[#02060a] transition-opacity duration-[1500ms] ease-out",
          phase === "fade" && "opacity-0"
        )}
      />
      <div
        className={cn(
          "absolute inset-0 bf-noise opacity-55 mix-blend-screen transition-opacity duration-[1200ms]",
          phase === "fade" && "opacity-0"
        )}
      />
      <div
        className={cn(
          "absolute inset-0 bf-scanlines opacity-55 transition-opacity duration-[1200ms]",
          phase === "fade" && "opacity-0"
        )}
      />
      <div
        className={cn(
          "pointer-events-none absolute inset-0 transition-opacity duration-[1500ms]",
          phase === "fade" && "opacity-0"
        )}
      >
        <div className="bf-green-orb absolute inset-0 opacity-70" />
        <div className="bf-amber-orb absolute inset-0 opacity-80" />
      </div>
      <div
        className={cn(
          "absolute inset-0 transition-opacity duration-[1200ms]",
          phase === "intro" ? "bf-flicker-strong" : "bf-flicker-soft"
        )}
        style={{ opacity: phase === "fade" ? 0 : undefined }}
      />

      <div className="relative flex h-full w-full items-center justify-center px-6">
        <div
          className={cn(
            "flex max-w-3xl flex-col items-center gap-8 text-center transition-all duration-[1500ms] ease-out",
            phase === "intro" && "opacity-0 translate-y-2",
            phase === "reveal" && "opacity-100 translate-y-0",
            phase === "fade" && "opacity-0 -translate-y-3 scale-95 blur-[1.5px]"
          )}
        >
          <h1 className="text-[2.6rem] sm:text-5xl md:text-6xl lg:text-[5.4rem] font-black uppercase tracking-[0.35em] text-white drop-shadow-[0_0_28px_rgba(255,255,255,0.25)]">
            BLACK FRIDAY
          </h1>

          <div className="flex flex-col items-center gap-5 text-white">
            <span className="inline-flex items-center rounded-full border border-white/25 px-4 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.3rem] text-white/80">
              Deal
            </span>
            <span className="text-[3.6rem] sm:text-[5.2rem] md:text-[6.2rem] lg:text-[7.2rem] font-black leading-none text-white">
              $25
            </span>
            <span className="text-sm sm:text-base font-semibold uppercase tracking-[0.5rem] text-white/80">
              Lifetime Access
            </span>
          </div>
        </div>

        <button
          onClick={finishIntro}
          aria-label="Skip intro animation"
          className="absolute right-8 top-8 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.35rem] text-white/60 transition hover:text-white"
        >
          Skip
        </button>
      </div>
    </div>
  );
}
